import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../../lib/baseQueries";
import {
  type ILimitsResponse,
  type ISubscription,
  type ITransaction,
  type IAddOnPreview,
} from "./Setting.types";

export const settingApi = createApi({
  reducerPath: "settingApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["BillingLimits", "Subscription", "Transactions"],
  endpoints: (builder) => ({
    getLimits: builder.query<ILimitsResponse, string>({
      query: (userId) => ({ url: `/v1/billing/limits/${userId}` }),
      providesTags: ["BillingLimits"],
    }),

    getSubscription: builder.query<ISubscription, void>({
      query: () => ({ url: "/v1/billing/subscription" }),
      providesTags: ["Subscription"],
    }),

    getTransactions: builder.query<ITransaction[], void>({
      query: () => ({ url: "/v1/billing/transactions" }),
      providesTags: ["Transactions"],
    }),

    subscribe: builder.mutation<
      { checkoutUrl: string; sessionId: string; resolvedRegion: string },
      { planName: string }
    >({
      query: (body) => ({
        url: "/v1/billing/subscribe",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Subscription", "BillingLimits"],
    }),

    purchaseAddOn: builder.mutation<
      { checkoutUrl: string; sessionId: string; resolvedRegion: string },
      { addOnType: string; quantity: number }
    >({
      query: (body) => ({
        url: "/v1/billing/addons/purchase",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BillingLimits"],
    }),

    previewAddOn: builder.mutation<
      IAddOnPreview,
      { addOnType: string; quantity: number }
    >({
      query: (body) => ({
        url: "/v1/billing/addons/preview",
        method: "POST",
        body,
      }),
    }),

    createCustomerPortalSession: builder.mutation<{ url: string }, void>({
      query: () => ({
        url: "/v1/billing/customer-portal",
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetLimitsQuery,
  useGetSubscriptionQuery,
  useGetTransactionsQuery,
  useSubscribeMutation,
  usePurchaseAddOnMutation,
  usePreviewAddOnMutation,
  useCreateCustomerPortalSessionMutation,
} = settingApi;
